import type {StepWizardChildProps} from "react-step-wizard";

import type {NumericSetterFunc, WizardNavigation} from "../components/wizard.types";

/**
 * Decimal precision for currency values
 */
export const CURRENCY_PRECISION = 2

/**
 * Decimal precision for VAT (ALV) values.
 */
export const VAT_PRECISION = 1

/**
 * Helper method that provides type guards for step wizard navigation functions.
 */
export function useWizardNavigation({
                                      previousStep,
                                      nextStep,
                                      isActive,
                                      currentStep,
                                      totalSteps,
                                    }: Partial<StepWizardChildProps>): WizardNavigation {
  if (!previousStep || !nextStep || typeof previousStep !== 'function' || typeof nextStep !== 'function') {
    throw new Error("Wizard step functions are not defined")
  }

  return {
    isStepCurrentlyActive: isActive ?? false,
    currentStep: currentStep ?? -1,
    totalSteps: totalSteps ?? 0,
    previousStep,
    nextStep
  }
}

/**
 * Helper setter for setting numeric values in the Planner wizard components.
 * @param setter 'useState' setter function.
 * @param value value to be set into 'useState' setter.
 */
export function setNumericValue(setter: NumericSetterFunc, value: number | undefined | string): void {
  if (typeof value === 'string') {
    value = isNaN(parseInt(value)) ? undefined : parseInt(value)
  } else {
    value = undefined
  }

  setter(value)
}

/**
 * TypeScript Type guard helper method to allow converting unknown types into record type.
 * @param value unknown typed object/value.
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Format numeric value into string representation with two decimals (i.e. into currency format).
 * @param val input number value.
 */
export function formatNumberToCurrencyValue(val: number | null): string {
  if (val == null) {
    return ''
  }

  return val.toLocaleString('fi', {style: 'currency', currency: 'EUR'})
}

/**
 * Format numeric value into string representation with one decimal (i.e. into VAT precision format).
 * @param val input number value.
 */
export function formatNumberToVatValue(val: number | null): string {
  if (val === null) {
    return ''
  }

  return val.toFixed(VAT_PRECISION)
}
